import app from '@/lib/app';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import Head from 'next/head';
import { Toaster } from 'react-hot-toast';
import type { AppPropsWithLayout } from 'types';
import mixpanel from 'mixpanel-browser';

import '@boxyhq/react-ui/dist/react-ui.css';
import '../styles/globals.css';
import { useEffect } from 'react';
import env from '@/lib/env';
import { Theme, applyTheme } from '@/lib/theme';
import { Themer } from '@boxyhq/react-ui/shared';
import { AccountLayout } from '@/components/layouts';

import '@xyflow/react/dist/style.css';

// 使用主题颜色 - hsl(243.3962 75.3555% 58.6275%) 紫色系
const primaryColor = '#6366f1'; // 对应 hsl(243.3962 75.3555% 58.6275%)
const primaryHover = '#4f46e5'; // 更深的紫色

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const { session, ...props } = pageProps;

  // Add mixpanel
  useEffect(() => {
    if (env.mixpanel.token) {
      mixpanel.init(env.mixpanel.token, {
        debug: true,
        ignore_dnt: true,
        track_pageview: true,
      });
    }

    if (env.darkModeEnabled) {
      applyTheme(localStorage.getItem('theme') as Theme);
    }
  }, []);

  const getLayout =
    Component.getLayout || ((page) => <AccountLayout>{page}</AccountLayout>);

  return (
    <>
      <Head>
        <title>{app.name}</title>
        <link rel="icon" href="/logo.png" />
      </Head>
      <SessionProvider session={session}>
        <Toaster toastOptions={{ duration: 4000 }} />
        <Themer
          overrideTheme={{
            '--primary-color': primaryColor,
            '--primary-hover': primaryHover,
            '--primary-color-50': '#eef2ff',
            '--primary-color-100': '#e0e7ff',
            '--primary-color-200': '#c7d2fe',
            '--primary-color-300': '#a5b4fc',
            '--primary-color-500': primaryColor,
            '--primary-color-600': primaryHover,
            '--primary-color-700': '#4338ca',
            '--primary-color-800': '#3730a3',
            '--primary-color-900': '#312e81',
            '--primary-color-950': '#1e1b4b',
          }}
        >
          {getLayout(<Component {...props} />)}
        </Themer>
      </SessionProvider>
    </>
  );
}

export default appWithTranslation<never>(MyApp);
